import { all, fork, put, call, takeLatest, select } from 'redux-saga/effects';
import * as authTypes from './authTypes';
import * as authActions from './authActions';
import * as appActions from '../../app/store/appActions'
import { setAppLoading } from 'src/modules/app/store/appActions';
import * as authApi from '../api/authApi';
import storage from 'src/utils/storageUtils';
import { push } from 'redux-first-history';
import { authPages } from 'src/config';
import { publicPages } from 'src/config';
import { useSelector } from 'react-redux';
import { getUserFromToken } from 'src/DecodeToken';
import { useEffect } from 'react';
import { getClinics } from 'src/modules/app/api/appApi';
import getPlatform from 'src/utils/getPlatform';

function* handleLogin({ data, resolve, reject }) {
  try {
    console.log('Attempting login');
    const res = yield call(authApi.loginUser, {
      email: data.email,
      password: data.password,
    });

    const token = res?.token;
    if (!token) throw new Error("Token not received from login");

    // Clear any stale session data from previous logins
    storage.del('CLINIC_ID');
    storage.del('DOCTORNAME');

    storage.set('TOKEN', token);
    const doctorId = getUserFromToken();

    const clinicsRes = yield call(getClinics);
    const clinics = clinicsRes?.data || [];

    if (clinics.length > 0) {
      const clinicId = clinics[0].id;
      storage.set('CLINIC_ID', clinicId);
      yield put(appActions.setCurrentClinics({ clinicId, doctorId }));
    }
    yield put(authActions.logDeviceInfo({
      userId: doctorId,
      appVersion: '1.0.0',
      platform: getPlatform(),
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    }));
    console.log('[DeviceLog] logDeviceInfo dispatched in handleLogin');

    yield put(authActions.restoreSession());
    resolve(true);
  } catch (error) {
    storage.del('TOKEN');
    storage.del('CLINIC_ID');
    storage.del('DOCTORNAME');
    reject(error);
  }
}


function* handleLogout() {
  storage.del('TOKEN');
  storage.del('CLINIC_ID');
  storage.del('DOCTORNAME');
  storage.del('TENANT_HASH');
  sessionStorage.removeItem('DEVICE_LOGGED');
  yield put(push('/auth/login'));
}


function* restoreSession() {
  const pathname = yield select((state) => state.router?.location?.pathname || '');
  const isPublicPage = (path) =>
    publicPages.some((publicPath) => path.toLowerCase().startsWith(publicPath));

  const token = storage.get('TOKEN');
  console.log('[AuthSaga] token:', token);
  console.log('[AuthSaga] pathname:', pathname);

  if (isPublicPage(pathname)) {
    yield put(setAppLoading(false));
    return;
  }

  try {
    if (token && token !== '') {
      const res = yield call(authApi.getProfile);

      if (res?.user) {
        storage.set('DOCTORNAME', res.user.personalInfo.fullName);
        yield put(authActions.setAuthUser(res.user));
        yield put(authActions.setAuthUserPermissions(res.permissions));

        // Use the user ID from the profile response (most reliable source)
        const doctorId = res.user.id || res.user._id || getUserFromToken();
        let clinicId = storage.get('CLINIC_ID');

        // If clinicId is missing, fetch clinics and store it
        if (!clinicId) {
          try {
            const clinicsRes = yield call(getClinics);
            const clinics = clinicsRes?.data || [];
            if (clinics.length > 0) {
              clinicId = clinics[0].id;
              storage.set('CLINIC_ID', clinicId);
              yield put(appActions.setCurrentClinics({ clinicId, doctorId }));
            } else {
              console.log('[AuthSaga] No clinics found for user, redirecting to onboarding');
              yield put(push('/public/onboards'));
              return;
            }
          } catch (clinicError) {
            console.warn('[AuthSaga] Failed to fetch clinics:', clinicError);
          }
        }

        // Redirect to dashboard if on auth page
        if (authPages.includes(pathname) && clinicId && doctorId) {
          yield put(push(`/${clinicId}/${doctorId}/dashboard`));
        }
      } else {
        storage.del('TOKEN');
        storage.del('CLINIC_ID');
        storage.del('DOCTORNAME');
        yield put(push('/auth/login'));
      }
    } else {
      yield put(push('/auth/login'));
    }
  } catch (error) {
    console.log('[AuthSaga] restoreSession failed:', error);
    storage.del('TOKEN');
    storage.del('CLINIC_ID');
    storage.del('DOCTORNAME');
    yield put(push('/auth/login'));
  } finally {
    yield put(setAppLoading(false));
  }
}

function* handleLogDevice(action) {
  try {
    console.log('[DeviceLog] Payload received in saga:', action.payload);

    if (sessionStorage.getItem('DEVICE_LOGGED')) return;

    const { data, resolve, reject } = action.payload;

    const response = yield call(authApi.logDevice, data);
    console.log('[DeviceLog] API Response:', response);

    yield put(authActions.logDeviceInfoSuccess(response));

    sessionStorage.setItem('DEVICE_LOGGED', 'true');
    if (resolve) resolve(response);
  } catch (error) {
    console.warn('[DeviceLog] Failed to log device:', error?.message || error);
    yield put(authActions.logDeviceInfoFailure(error));
     action.payload.reject(error);
  }
}

function* sendOtpSaga(action) {
  const { contactNo, resolve, reject } = action.payload;
  try {
    console.log('Saga: Sending OTP with Payload:', action.payload);
    yield call(authApi.sendOtp, contactNo);
    console.log('OTP Sent Successfully');
    yield put({ type: authTypes.POST_OTP_SUCCESS });
    resolve();
  } catch (error) {
    console.error('Error in sendOtpSaga:', error);
    yield put({ type: authTypes.POST_OTP_FAILURE, payload: error });
    action.payload.reject(error);
  }
}

function* loginWithOtpSaga(action) {
  const { data, resolve, reject } = action.payload;
  try {
    const response = yield call(authApi.loginWithOtp, data);
    const token = response?.token;
    if (!token) {
      throw new Error('Token not found in response');
    }
    yield put({ type: authTypes.POST_LOGIN_WITH_OTP_SUCCESS, payload: token });
    storage.set('TOKEN', token);
    yield put(authActions.restoreSession());
    resolve(true);

  } catch (error) {
    console.error('Error in sendOtpSaga:', error);
    yield put({ type: authTypes.POST_LOGIN_WITH_OTP_FAILURE, payload: error });
    action.payload.reject(error);
  }
}

function* sendOtpToContactSaga(action) {
  const { data, resolve, reject } = action.payload;
  try {
    const response = yield call(authApi.sendOtpToContact, data);
    const token = response?.token;
    yield put({ type: authTypes.POST_CONTACT_OTP_SUCCESS, payload: token });
    storage.set('TOKEN', token);
    yield put(authActions.restoreSession());
    resolve();
  } catch (error) {
    yield put({ type: authTypes.POST_CONTACT_OTP_FAILURE, payload: error });
    reject(error);
  }
}

function* handleDisconnectCalendarById(action) {
  const { id, resolve, reject } = action.payload;
  try {
    const response = yield call(authApi.disconnectCalendarById, id);
    console.log("here saga delete response of message", response)

    if (response) {
      yield put(authActions.disconnectCalendarByIdSuccess(id));
      console.log("saga response success", response)
      if (resolve) resolve(response.data || {});
    } else {
      const error = new Error(response.data?.message || 'Error deleting message');
      yield put(authActions.disconnectCalendarByIdFailure(error));
      if (reject) reject(error);
    }
  } catch (error) {
    console.log(error);
    yield put(authActions.disconnectCalendarByIdFailure(error));
    if (reject) reject(error);
  }
}

export function* watchSagas() {
  yield takeLatest(authTypes.LOGIN, handleLogin);
  yield takeLatest(authTypes.RESTORE_SESSION, restoreSession);
  yield takeLatest(authTypes.LOGOUT, handleLogout);
  yield takeLatest(authTypes.POST_OTP_REQUEST, sendOtpSaga);
  yield takeLatest(authTypes.POST_LOGIN_WITH_OTP_REQUEST, loginWithOtpSaga);
  yield takeLatest(authTypes.POST_CONTACT_OTP_REQUEST, sendOtpToContactSaga);
  yield takeLatest(authTypes.DISCONNECT_CALENDAR_BY_ID_REQUEST, handleDisconnectCalendarById);
  yield takeLatest(authTypes.LOG_DEVICE_INFO, handleLogDevice);
}

export default function* runSagas() {
  yield all([fork(watchSagas)]);
}